/**
 * Multi-tenant admin UX.
 *
 * - TenantSwitcher  — dropdown to switch the active tenant scope (persists
 *                     in localStorage; defaults to "default")
 * - TenantUsersView — admin view showing users scoped to the selected tenant
 *
 * The tenant scope is read by admin views via the useTenantScope hook.
 * Each admin view that supports tenant scoping filters its data by the
 * selected tenantId.
 */

import { useState, createContext, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PageHeader, LoadingState, ErrorState, EmptyState } from "@/components/features/states";
import { Building2, Users, Plus, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { formatRelativeTime } from "@/lib/format";

// ------------------------------------------------------------------
// Tenant scope context — stores the selected tenantId for admin views.
// ------------------------------------------------------------------

const TENANT_KEY = "horndeliver.admin.tenantId";

interface TenantScopeCtx {
  tenantId: string;
  setTenantId: (id: string) => void;
}
const TenantScope = createContext<TenantScopeCtx>({
  tenantId: "default",
  setTenantId: () => {},
});

export function useTenantScope() {
  return useContext(TenantScope);
}

export function TenantScopeProvider({ children }: { children: React.ReactNode }) {
  // Lazy initializer reads localStorage once on mount — avoids the
  // setState-in-effect cascading-render lint warning.
  const [tenantId, setTenantIdState] = useState<string>(() => {
    if (typeof window === "undefined") return "default";
    return localStorage.getItem(TENANT_KEY) || "default";
  });
  const setTenantId = (id: string) => {
    setTenantIdState(id);
    if (typeof window !== "undefined") localStorage.setItem(TENANT_KEY, id);
  };
  return (
    <TenantScope.Provider value={{ tenantId, setTenantId }}>
      {children}
    </TenantScope.Provider>
  );
}

// ------------------------------------------------------------------
// TenantSwitcher — admin-only dropdown.
// ------------------------------------------------------------------

export function TenantSwitcher() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { tenantId, setTenantId } = useTenantScope();
  const [createOpen, setCreateOpen] = useState(false);

  const { data, refetch } = useQuery({
    queryKey: ["tenants"],
    queryFn: () => api.admin.tenants(),
    enabled: !!user?.isAdmin,
  });

  const tenants = data?.data?.tenants ?? [];
  const current = tenants.find((t: any) => t.id === tenantId);

  if (!user?.isAdmin) return null;

  return (
    <div className="flex items-center gap-2">
      <Building2 className="h-4 w-4 text-muted-foreground" />
      <Select value={tenantId} onValueChange={setTenantId}>
        <SelectTrigger className="w-[200px] h-8 text-xs">
          <SelectValue placeholder={t("tenant.select") || "Select tenant"} />
        </SelectTrigger>
        <SelectContent>
          {tenants.map((t: any) => (
            <SelectItem key={t.id} value={t.id} className="text-xs">
              <div className="flex items-center gap-2">
                <span>{t.name}</span>
                <Badge variant="outline" className="text-[10px]">{t._count?.users ?? 0}</Badge>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <CreateTenantDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(t) => {
          setTenantId(t.id);
          refetch();
        }}
      />
      <Button
        variant="outline"
        size="sm"
        className="h-8 px-2"
        onClick={() => setCreateOpen(true)}
        title={t("tenant.create") || "Create tenant"}
      >
        <Plus className="h-3.5 w-3.5" />
      </Button>
      {current && (
        <Badge variant="secondary" className="text-[10px]">
          {current.plan}
        </Badge>
      )}
    </div>
  );
}

function CreateTenantDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (tenant: any) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [plan, setPlan] = useState("free");
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!name || !slug) {
      toast.error(t("tenant.fillFields") || "Fill in name and slug");
      return;
    }
    setLoading(true);
    const res = await api.admin.createTenant({ name, slug, plan });
    setLoading(false);
    if (res.ok && res.data?.tenant) {
      toast.success(t("tenant.created") || "Tenant created");
      onCreated(res.data.tenant);
      setName("");
      setSlug("");
      onOpenChange(false);
    } else {
      toast.error(res.error || t("tenant.createFailed") || "Failed to create tenant");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("tenant.create") || "Create tenant"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium">{t("tenant.name") || "Name"}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corp" />
          </div>
          <div>
            <label className="text-sm font-medium">{t("tenant.slug") || "Slug"}</label>
            <Input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="acme-corp"
              className="font-mono"
            />
          </div>
          <div>
            <label className="text-sm font-medium">{t("tenant.plan") || "Plan"}</label>
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="business">Business</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleCreate} disabled={loading} className="w-full">
            {loading ? t("common.creating") || "Creating..." : t("tenant.create") || "Create tenant"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// TenantUsersView — admin view: users scoped to the selected tenant.
// ------------------------------------------------------------------

export function TenantUsersView() {
  const { t } = useTranslation();
  const { tenantId } = useTenantScope();
  const [search, setSearch] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "users-by-tenant", tenantId],
    queryFn: () => api.admin.usersByTenant(tenantId),
  });

  const users = (data?.data?.users ?? []).filter(
    (u) =>
      !search ||
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title={t("tenant.usersTitle") || "Tenant Users"}
        description={`${tenantId} — ${users.length} ${t("common.users") || "users"}`}
        icon={Users}
      />

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {t("tenant.scopedUsers") || "Tenant-scoped users"}
            </CardTitle>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("admin.searchUsers") || "Search users..."}
              className="max-w-xs"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={(error as any)?.message} />
          ) : users.length === 0 ? (
            <EmptyState
              icon={Users}
              title={t("tenant.noUsers") || "No users in this tenant"}
              description={t("tenant.noUsersDesc") || "Switch to a different tenant or create users."}
            />
          ) : (
            <div className="space-y-2">
              {users.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center gap-3 rounded-lg border p-3 hover:bg-accent/50 transition-colors"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarFallback>{u.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{u.name}</span>
                      {u.isAdmin && (
                        <Badge variant="default" className="text-[10px] gap-1">
                          <Shield className="h-2.5 w-2.5" /> Admin
                        </Badge>
                      )}
                      {u.flagged && (
                        <Badge variant="destructive" className="text-[10px]">Flagged</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <Badge variant="outline" className="text-xs capitalize">{u.activeRole}</Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatRelativeTime(u.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
